// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { extractErrorMessage, useEvolvePost } from '@evolvemep/api-ts';
import { Button, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
// oxlint-disable-next-line no-restricted-imports
import { useNavigate, useSearchParams } from 'react-router';

import { NewPasswordInput } from 'app/Layout/HeaderBar/ProfileDropdown/ResetPasswordModal/NewPasswordInput';

import { AuthFormWrapper } from './components/AuthFormWrapper';
import type { LoginErrorCode } from './components/LoginErrorAlert';

export const ResetPassword = () => {
  const theme = useMantineTheme();
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const { apiCall } = useEvolvePost('admin/authentication/confirmForgotPassword', { alertOnError: false });
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<LoginErrorCode | string>();
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  const form = useForm<{ newPassword: string }>({
    initialValues: { newPassword: '' },
  });

  const resetPasswordHandler = async ({ newPassword }: typeof form.values) => {
    setLoading(true);
    const username = queryParams.get('username');
    const confirmationCode = queryParams.get('code');

    if (username && confirmationCode) {
      try {
        await apiCall({ confirmationCode, username, newPassword });
        void navigate('/auth/login', { state: { resetPassword: true } });
      } catch (error) {
        setErrorCode(extractErrorMessage(error));
        setLoading(false);
      }
    } else {
      setErrorCode('UnknownException');
    }
  };

  return (
    <AuthFormWrapper
      title="Create new password"
      onSubmit={resetPasswordHandler}
      form={form}
      errorCode={errorCode}
      onCloseError={() => setErrorCode(undefined)}
    >
      <NewPasswordInput
        {...form.getInputProps('newPassword')}
        disabled={loading}
        setPasswordIsValid={setPasswordIsValid}
      />
      <Button type="submit" fullWidth color={`${theme.primaryColor}.9`} loading={loading} disabled={!passwordIsValid}>
        Submit
      </Button>
    </AuthFormWrapper>
  );
};
